/* «Что скажет» (APP-SPEC-4 §4): поле текста с микрофоном, поводами, правилом, счётчиком и «во весь экран» (§4.1);
   рамка записи речи вместо поля и «Написать текстом» (§4.2); карточка «Своя речь из видео» и «Новые слова» (§4.3); заметка (§4.4). */
import { useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import { PromptChip } from '../../../ui/PromptChip';
import { TextEditor } from '../../../ui/TextEditor';
import { SpeechAudioInput } from '../../tts/SpeechAudioInput';
import { StudioIcon } from '../StudioIcon';
import { StudioUploadRights } from '../StudioUploadRights';
import type { StudioForm } from '../studio-api';
import type { CircleFormState } from './CircleFieldInput';
import { mmss } from './order-tail-model';
import { ToolIcon } from './ToolIcon';

const expand = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);
/** Поле «Что скажет?» (общий .box): текст, микрофон справа сверху, поводы, правило, счётчик «n / предел», «во весь экран». */
export function CircleTextBox({
  value,
  onChange,
  min,
  max,
  placeholder,
  disabled,
  occasions,
  occasion,
  onOccasion,
  onMic,
  title,
}: {
  value: string;
  onChange(value: string): void;
  min: number;
  max: number;
  placeholder: string;
  disabled: boolean;
  /** Поводы [подпись, готовый текст]; пусто — ряда нет */
  occasions: Array<[string, string]>;
  occasion: string;
  onOccasion(label: string, text: string): void;
  /** Микрофон в поле: речь записью (нет — форма не публикует запись) */
  onMic?: () => void;
  title: string;
}) {
  const [full, setFull] = useState(false);
  const count = Array.from(value.trim()).length;
  const cls = !count ? '' : count > max || (min && count < min) ? ' bad' : ' ok';
  const chips = (editor: boolean) =>
    !!occasions.length && (
      <div className={`chips${editor ? ' bc-edch' : ' in'}`} id={editor ? undefined : 'apOcc'}>
        {occasions.map(([label, text]) => (
          <PromptChip key={label} label={uiText(label)} selected={occasion === label && value.trim() === text} disabled={disabled} onClick={() => onOccasion(label, text)} />
        ))}
      </div>
    );
  return (
    <>
      <div className={`box tc-box${count > max ? ' bad' : ''}`} id="apBox">
        <textarea
          id="apTa"
          value={value}
          maxLength={max + 50}
          placeholder={uiText(placeholder)}
          aria-label={uiText('Что скажет')}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
        />
        {onMic && (
          <button className="tc-mic" type="button" aria-label={uiText('Речь голосом: запись или файл')} disabled={disabled} onClick={onMic}>
            <StudioIcon kind="mic" />
          </button>
        )}
        {chips(false)}
        <div className="foot">
          <span>{uiText('Без политики и запрещённых тем')}</span>
          <span className={`cnt${cls}`} id="apCnt">
            {count} / {max}
          </span>
          <button className="bx-full" type="button" aria-label={uiText('Во весь экран')} disabled={disabled} onClick={() => setFull(true)}>
            {expand}
          </button>
        </div>
      </div>
      {full && (
        <TextEditor
          label={uiFormat('Что скажет · {0}', uiText(title))}
          value={value}
          limit={max}
          disabled={disabled}
          onChange={onChange}
          onClose={() => setFull(false)}
          toolbar={chips(true)}
        />
      )}
    </>
  );
}
/** Рамка записи речи (BotCore.upload, вид audio) и «Написать текстом». */
export function CircleAudioBox({
  field,
  state,
  audioOnly,
  onText,
  onDuration,
}: {
  field: StudioForm['fields'][number];
  state: CircleFormState;
  /** «Кружок из аудио»: подпись про анимацию */
  audioOnly: boolean;
  onText?: () => void;
  onDuration?(ms: number): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const maximumBytes = Number.isSafeInteger(field.upload?.max_bytes) && field.upload!.max_bytes! > 0 ? field.upload!.max_bytes : undefined;
  const uploaded = !!state.uploadIds[field.key] || !!state.inputs[field.key];
  return (
    <div className="tc-aud" id="apAudW">
      <div className={`bcu circle-aud${uploaded ? ' has' : ''}`}>
        <div className="bcu-e">
          <span className="bcu-ic">
            <StudioIcon kind="mic" />
          </span>
          <b className="bcu-t">{uiText(uploaded ? (file?.name ?? 'Запись') : 'Запишите или загрузите речь')}</b>
          <span className="bcu-s">{uiText(audioOnly ? 'Анимация оживёт под ваш звук' : 'Губы повторят вашу речь, до минуты')}</span>
          {!state.rights && <StudioUploadRights state={state} />}
          {!uploaded ? (
            <SpeechAudioInput
              file={file}
              disabled={state.locked || !state.rights}
              accept={field.upload?.accept ?? []}
              maximumMs={field.upload?.max_duration_ms ?? 60000}
              maximumBytes={maximumBytes}
              onFile={next => {
                setFile(next);
                const probe = typeof Audio === 'function' ? new Audio() : null;
                if (probe && typeof URL.createObjectURL === 'function') {
                  const url = URL.createObjectURL(next);
                  probe.preload = 'metadata';
                  probe.onloadedmetadata = () => {
                    if (Number.isFinite(probe.duration)) onDuration?.(Math.round(probe.duration * 1000));
                    URL.revokeObjectURL(url);
                  };
                  probe.src = url;
                }
                void state.upload(field.key, next);
              }}
            />
          ) : (
            <div className="bcu-fa">
              <button
                type="button"
                className="bcu-pl gl"
                disabled={state.locked}
                onClick={() => {
                  state.clearUpload(field.key);
                  setFile(null);
                  onDuration?.(0);
                }}
              >
                <StudioIcon kind="mic" />
                <span>{uiText('Заменить')}</span>
              </button>
              <button
                type="button"
                className="bcu-pl gl"
                disabled={state.locked}
                onClick={() => {
                  state.clearUpload(field.key);
                  setFile(null);
                  onDuration?.(0);
                }}
              >
                <StudioIcon kind="close" />
                <span>{uiText('Убрать')}</span>
              </button>
            </div>
          )}
          {state.uploadIds[field.key] && !state.inputs[field.key] && (
            <button type="button" className="ap-qt" disabled={state.busy || state.locked} onClick={() => void state.upload(field.key, null)}>
              {uiText('Продолжить загрузку')}
            </button>
          )}
          <small className="bcu-n">{uiText('MP3, WAV или M4A')}{maximumBytes ? `, ${uiFormat('до {0} МБ', Math.round(maximumBytes / 1024 / 1024))}` : ''}</small>
        </div>
      </div>
      {onText && (
        <button className="ap-qt" type="button" disabled={state.locked} onClick={onText}>
          <StudioIcon kind="edit" />
          <span>{uiText('Написать текстом')}</span>
        </button>
      )}
    </div>
  );
}
/** Своя речь из видео: что прозвучит, длительность фрагмента и «Новые слова». */
export function CircleKeepCard({ voiceName, durationMs, onNewWords, disabled }: { voiceName: string | null; durationMs: number; onNewWords?: () => void; disabled: boolean }) {
  return (
    <div className="tc-keep" id="apKeep">
      <span className="ic">
        <StudioIcon kind="wave" />
      </span>
      <span className="m">
        <b>{uiText('Своя речь из видео')}</b>
        <span>
          {voiceName ? uiFormat('Ваши слова голосом «{0}»', voiceName) : uiText('Слова и голос как в ролике')}
          {durationMs > 0 && (
            <>
              {' · '}
              <span className="mono">{mmss(durationMs / 1000)}</span>
            </>
          )}
        </span>
      </span>
      {onNewWords && (
        <button className="ap-qt" type="button" disabled={disabled} onClick={onNewWords}>
          <StudioIcon kind="edit" />
          <span>{uiText('Новые слова')}</span>
        </button>
      )}
    </div>
  );
}
/** Заметка под полем — только о важном (noteText). */
export function CircleNote({ kind, text }: { kind: 'mute' | 'warn'; text: string }) {
  return (
    <div className={`note ${kind} circle-note`} role="status">
      <ToolIcon kind={kind === 'warn' ? 'warn' : 'info'} />
      <span>{uiText(text)}</span>
    </div>
  );
}
